"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EffluentTracker = void 0;
const datamodel_core_1 = require("@iotv/datamodel-core");
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const helpers_1 = require("@turf/helpers");
const turf_1 = require("@turf/turf");
const index_1 = require("../../DefinitionAndHelpers/index");
const typeName = 'EffluentTracker';
const dev = process.env.REACT_APP_DEBUG;
const debug = dev && false;
class EffluentTracker extends datamodel_core_1.DeviceEnabledType {
    constructor(ob) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        super(ob.type || typeName, ob);
        this.throwDistance = (_a = ob.throwDistance) !== null && _a !== void 0 ? _a : null;
        this.throwArea = (_b = ob.throwArea) !== null && _b !== void 0 ? _b : null;
        this.updated_at = (_c = ob.updated_at) !== null && _c !== void 0 ? _c : null;
        this.location = ob.location;
        this.path = (_d = ob.path) !== null && _d !== void 0 ? _d : null;
        this.filteredPath = (_e = ob.filteredPath) !== null && _e !== void 0 ? _e : null;
        this.spreading = (_f = ob.spreading) !== null && _f !== void 0 ? _f : false;
        this.flow = ob.flow;
        this.pressure = (_g = ob.pressure) !== null && _g !== void 0 ? _g : null;
        this.lastIrrigated = (_h = ob.lastIrrigated) !== null && _h !== void 0 ? _h : null;
        this.radius = (_j = ob.radius) !== null && _j !== void 0 ? _j : null;
        this.runDifference = ob.runDifference;
        this.missingLocationCnt = ob.missingLocationCnt;
        this.isOperating = (_k = ob.isOperating) !== null && _k !== void 0 ? _k : false;
        this.pressureSum = (_l = ob.pressureSum) !== null && _l !== void 0 ? _l : null;
        this.lastPressure = (_m = ob.lastPressure) !== null && _m !== void 0 ? _m : null;
        this.pressureList = (_o = ob.pressureList) !== null && _o !== void 0 ? _o : null;
        this.currentPumpState = ob.currentPumpState;
        this.pumpState = (_p = ob.pumpState) !== null && _p !== void 0 ? _p : null;
        this.onExclusionArea = ob.onExclusionArea;
        this.mappedRun = (_q = ob.mappedRun) !== null && _q !== void 0 ? _q : null;
        this.mappedPath = (_r = ob.mappedPath) !== null && _r !== void 0 ? _r : null;
        this.eventSize = this.path ? this.path.length : null;
        this.name = ob.name;
        delete this.updatedAt;
    }
    /** Functions to update spreading path details */
    /**
     * Default method. Accepts all runs
     * Method to get relevant runs
     * @param runList
     * @returns runs of correct run type
     */
    getRelevantRuns(runList) {
        debug && console.log('Using default - getRelevantRuns');
        return runList;
    }
    /**
     * Filters out outliers in a path
     * Formats datapoints to be valued
     * @returns Updates and returns a filtered version fo the path
     */
    filterOutPathOutliers() {
        debug && console.log('Should be using default - filterOutPathOutliers');
        if (this.path) {
            const filtered = index_1.EffluentSpreaderHelper.parseLocationOutliers(this.path);
            this.filteredPath = filtered;
            return filtered;
        }
    }
    /**
     * Given a set of paths, get the closest run associated with the active data,
     * - Should be for travelling and lateral only
     * @param runs
     * @param parsedPath
     * @returns the path closests to the path of the spreader
     */
    getBestMatchedRun(runs, rawPath = this.path) {
        debug && console.log('Using default - getBestMatchedRun');
        const parsedPath = rawPath !== null && rawPath !== void 0 ? rawPath : this.path;
        if (runs.length === 0 || parsedPath === null || parsedPath.length === 0) {
            return undefined;
        }
        //turf current path
        const currentPathTurf = index_1.EffluentSpreaderHelper.convertPositionToTurf(parsedPath).map((tf) => (0, helpers_1.point)(tf));
        let runScore = runs
            .map((run) => {
            let runPoint = run.path;
            if (runPoint === undefined || runPoint.length < 2) {
                return undefined;
            }
            let turfedRun;
            turfedRun = (0, helpers_1.lineString)(index_1.EffluentSpreaderHelper.convertPositionToTurf(runPoint || []));
            let distance = currentPathTurf.reduce((acc, cur) => {
                let dis = (0, turf_1.pointToLineDistance)(cur, turfedRun, { units: 'meters' });
                acc += dis;
                return acc;
            }, 0);
            return {
                distance,
                average: distance / (parsedPath.length || 1),
                run,
            };
        })
            .filter((val) => val !== undefined)
            .sort((a, b) => a.distance - b.distance);
        return runScore[0];
    }
    /**
     * Get the area that the tracker most belongs to
     * @param areas the differents areas to check the spreader against
     */
    getRelevantArea(areas) {
        // let points = this.path ?? this.location ?? undefined;
        let points = this.mappedPath && this.mappedPath.length > 0
            ? this.mappedPath
            : this.path && this.path.length > 0
                ? this.path
                : this.location
                    ? [this.location]
                    : undefined;
        if (points && areas.length > 0) {
            const pointsList = index_1.EffluentSpreaderHelper.convertPositionToTurf(points);
            const areaCounter = areas
                .map((area) => {
                const areaPolygon = (0, helpers_1.polygon)([index_1.EffluentSpreaderHelper.convertPositionToTurf(area.points)]);
                const count = pointsList.reduce((acc, point) => {
                    if ((0, turf_1.booleanPointInPolygon)(point, areaPolygon)) {
                        acc++;
                    }
                    return acc;
                }, 0);
                return { area, count };
            })
                .filter((val) => val.count !== 0)
                .sort((a, b) => b.count - a.count); //filter out areas that had no intercept, then orders
            if (areaCounter.length > 0) {
                const mostCommon = areaCounter[0].area;
                return mostCommon;
            }
        }
        return undefined;
    }
    /**
     * Given active path data, convert it into datapoints on the mapped run
     * - Should be used for Travelling and Lateral
     * @param path of the Spreader
     * @param run of the EffluentPath
  
     * @returns
     */
    getPathOnRun(path, run) {
        debug && console.log('Using default - getPathOnRun');
        if (run) {
            return index_1.EffluentSpreaderHelper.getMappedRunPath(path, run.path);
        }
        else {
            const x = path.map((p) => p.lat);
            const y = path.map((p) => p.lng);
            // let mapped = findLineByLeastSquares(x, y) || path;
            let mapped = index_1.EffluentSpreaderHelper.findLineByFirstLast(path) || path;
            return mapped;
        }
    }
    /**
     * Given a list of paths, returns the most expected run
     * @param runs
     * @returns
     */
    getExpectedRun(runs) {
        debug && console.log('Should be using default - getExpectedRun');
        const relevantRuns = this.getRelevantRuns(runs);
        const filteredPath = this.filterOutPathOutliers(); // filtered path is unreliable. When it is, swap out this.path
        const bestMatch = this.getBestMatchedRun(relevantRuns, this.path); // filtered path is unreliable. When it is, swap out this.path
        if (bestMatch !== undefined && this.path !== null) {
            const pointsOnRun = this.getPathOnRun(this.path, bestMatch.run); // filtered path is unreliable. When it is, swap out this.path
            // construct acceptable range
            const runDistance = bestMatch.run.throwDistance + bestMatch.run.radius;
            if (bestMatch.average < runDistance * 1.2) {
                this.runDifference = bestMatch.distance;
                return { run: bestMatch.run, path: pointsOnRun };
            }
        }
    }
    /**
     * Method to reset the spreading details
     *
     * Only to be used after a SpreadingEvent and DeadZone is created
     */
    resetSpreadingDetails() {
        this.path = [];
        this.filteredPath = [];
        this.spreading = false;
        this.mappedRun = '';
        this.mappedPath = [];
        this.runDifference = 0;
        this.pressureList = [];
        this.pumpState = [];
        this.moistureReadingsList = [];
    }
    /**
     * Get the PK's of the area's that overlap with the spreading event
     * @param areas
     */
    getOverlappingAreas(areas, spreadingArea) {
        debug && console.log('Should be using default - getOverlappingAreas');
        const overlapsArr = areas
            .map((paddockArea) => {
            if (paddockArea.points && paddockArea.points.length > 2 && index_1.EffluentSpreaderHelper.overlapArea(paddockArea.points, spreadingArea) !== 0) {
                return paddockArea.pk;
            }
        })
            .filter((val) => val !== undefined);
        return overlapsArr;
    }
    /** Support functions for calculating spread area */
    /**
     * Function to get the spread LatLngs, and area (m^2), of the raw run
     * @returns LatLngs and area (m^2) obj
     */
    getSpreadOfRawRun(relevantRun, rawPath) {
        var _a, _b;
        if (rawPath === void 0) { rawPath = (_a = this.mappedPath) !== null && _a !== void 0 ? _a : this.path; }
        debug && console.log('Using default - getSpreadOfRawRun');
        const parsedPath = (_b = rawPath !== null && rawPath !== void 0 ? rawPath : this.mappedPath) !== null && _b !== void 0 ? _b : this.path;
        if (parsedPath && parsedPath.length > 1) {
            let turfBoundary = index_1.EffluentSpreaderHelper.getLineBoundaries(parsedPath, this.throwDistance || index_1.EFFLUENT_PATH_DEFAULT_RANGE);
            if (turfBoundary) {
                let positions = index_1.EffluentSpreaderHelper.convertTurfToPosition(turfBoundary.geometry.coordinates[0]);
                let area = index_1.EffluentSpreaderHelper.getAreaMeter(turfBoundary);
                return { positions, area };
            }
        }
        return undefined;
    }
    /**
     * Function to get the spread LatLngs, and area (m^2), of the filtered run
     * Default to raw if not provided
     * @returns LatLngs and area (m^2) obj
     */
    getSpreadOfFilteredRun(relevantRun, rawPath) {
        var _a, _b;
        if (rawPath === void 0) { rawPath = (_a = this.filteredPath) !== null && _a !== void 0 ? _a : this.path; }
        debug && console.log('Should be using default - getSpreadOfRawRun');
        const parsedPath = (_b = rawPath !== null && rawPath !== void 0 ? rawPath : this.filteredPath) !== null && _b !== void 0 ? _b : this.path;
        return this.getSpreadOfRawRun(relevantRun, parsedPath); //relevant is not needed as it is already used
    }
    /**
     * Should be for travelling only
     * Function to get the spread LatLngs, and area (m^2), of the mapped run
     * @returns LatLngs and area (m^2) obj
     */
    getSpreadOfMappedRun(relevantRun, rawPath = this.mappedPath) {
        var _a;
        debug && console.log('Should be using default - getSpreadOfMappedRun');
        const parsedPath = (_a = rawPath !== null && rawPath !== void 0 ? rawPath : this.mappedPath) !== null && _a !== void 0 ? _a : this.path;
        // check if it can even be 'mapped'
        if (parsedPath && parsedPath.length > 1) {
            let mappedPoints = parsedPath;
            if (relevantRun) {
                mappedPoints = this.getPathOnRun(parsedPath, relevantRun);
            }
            return this.getSpreadOfRawRun(relevantRun, mappedPoints);
        }
        return undefined;
    }
    /** Main functions called outside */
    /**
     *
     * @todo: Make this function that updates the details of the tracker
     */
    // updateTrackerFromActive() {}
    /**
     * Method to update the effluent spreader object
     * Internally updates:
     * - filteredPath (getExpectedRun -> filterOutPathOutliers)
     * - runDifference (getExpectedRun)
     * - mappedRun (this)
     * - mappedPath (this)
     * - last irrigated (this)
     * @param runs
     */
    updateMappedRun(runs, lastTimestamp) {
        debug && console.log('Should be using default - updateMappedRun');
        const runPath = this.getExpectedRun(runs);
        if (runPath !== undefined) {
            const mappedPathOnRun = this.getPathOnRun(runPath.path, runPath.run);
            this.mappedRun = runPath.run.pk;
            this.mappedPath = mappedPathOnRun;
        }
        else if (this.path !== null) {
            // using generic
            this.mappedPath = this.getPathOnRun(this.path);
        }
        this.lastIrrigated = lastTimestamp;
    }
    checkOverlapOnExclusions(exclusionList, previousAreas) {
        if (this.path !== undefined) {
            const spreadArea = this.getSpreadOfRawRun(undefined, this.path);
            let overlappedAreas = [];
            if (spreadArea !== undefined) {
                // only check other spreading areas if its more than the first two check-ins, as it may be starting off from a stopped event
                if (this.path && this.path.length > 2) {
                    // check if it was on th previousAreas
                    let previousAreasMatch = previousAreas
                        .map((deadZone) => {
                        if (deadZone.points.length > 3) {
                            let overlapArea = index_1.EffluentSpreaderHelper.overlapArea(spreadArea.positions, deadZone.points);
                            if (overlapArea > index_1.ACCEPTABLE_COVERAGE) {
                                return deadZone.name;
                            }
                        }
                        return undefined;
                    })
                        .filter((val) => val !== undefined);
                    overlappedAreas.push(...previousAreasMatch);
                }
                let exclusionListMatch = exclusionList
                    .map((exclusion) => {
                    if (exclusion.points.length > 3) {
                        let overlapArea = index_1.EffluentSpreaderHelper.overlapArea(spreadArea.positions, exclusion.points);
                        if (overlapArea > index_1.ACCEPTABLE_COVERAGE) {
                            return exclusion.name;
                        }
                    }
                    return undefined;
                })
                    .filter((val) => val !== undefined);
                overlappedAreas.push(...exclusionListMatch);
                if (overlappedAreas.length > 0) {
                    this.onExclusionArea = overlappedAreas.join(', ');
                    return;
                }
            }
        }
        this.onExclusionArea = undefined;
    }
    /**
     * Get run reccorded in effluent object, indicated in key mappedRun
     * @param runs
     * @returns run that matches pk of mappedRun
     */
    getMatchedRun(runs) {
        debug && console.log('Should be using default - getMatchedRun');
        if (runs && this.mappedRun) {
            let find = runs.find((run) => run.pk === this.mappedRun);
            return find;
        }
        return undefined;
    }
    /**
     * Function to get the spread LatLngs, and area (m^2), of all 3 conditions
     */
    getSpreadOfAllRuns(relevantRun) {
        debug && console.log('Should be using default - getSpreadOfAllRuns');
        // spread for raw
        let mapped = this.getSpreadOfMappedRun(relevantRun);
        let filtered = undefined;
        let raw = undefined;
        if (mapped === undefined) {
            filtered = this.getSpreadOfFilteredRun(relevantRun);
            raw = this.getSpreadOfRawRun(relevantRun);
        }
        // let raw = this.getSpreadOfRawRun(relevantRun)
        // spread for filtered
        // spread for mapped
        return { raw, filtered, mapped };
    }
    /**
     * Create spreading event using the details of this tracker
     */
    createSpreadingEventAndDeadZone(areas, runs, manual = false) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        if (this.path !== null && this.path.length !== 0) {
            const startPoint = this.path[0];
            const endPoint = this.path[this.path.length - 1];
            const startTime = (_a = startPoint.timestamp) !== null && _a !== void 0 ? _a : 0;
            const endTime = (_b = endPoint.timestamp) !== null && _b !== void 0 ? _b : 0;
            const distance = index_1.EffluentSpreaderHelper.measuredDistance(startPoint, endPoint);
            const duration = (endTime - startTime) / iotv_v3_types_1.Milliseconds.MINUTE;
            const amountSpread = duration * (this.flow || 0);
            const deleteTime = (0, datamodel_core_1.getTTLSeconds)(duration);
            const mappedRun = this.getMatchedRun(runs);
            const areaMapper = this.getSpreadOfAllRuns(mappedRun);
            const area = (_h = (_f = (_d = (_c = areaMapper.mapped) === null || _c === void 0 ? void 0 : _c.area) !== null && _d !== void 0 ? _d : (_e = areaMapper.filtered) === null || _e === void 0 ? void 0 : _e.area) !== null && _f !== void 0 ? _f : (_g = areaMapper.raw) === null || _g === void 0 ? void 0 : _g.area) !== null && _h !== void 0 ? _h : 1;
            const coverage = (_p = (_m = (_k = (_j = areaMapper.mapped) === null || _j === void 0 ? void 0 : _j.positions) !== null && _k !== void 0 ? _k : (_l = areaMapper.filtered) === null || _l === void 0 ? void 0 : _l.positions) !== null && _m !== void 0 ? _m : (_o = areaMapper.raw) === null || _o === void 0 ? void 0 : _o.positions) !== null && _p !== void 0 ? _p : this.path;
            // get overlapping areas
            let paddocks = areas.filter((area) => area.subType === undefined || area.subType === datamodel_core_1.AreaSubTypeEnum.Paddock);
            const overlappedAreas = this.getOverlappingAreas(paddocks, coverage);
            const concentration = amountSpread / area;
            const irrigatedArea = coverage;
            const applicationDepth = (concentration / 1000) * 100; //convert to meters, then to cm
            const pressure = this.pressureList && this.pressureList.length > 1
                ? this.pressureList.reduce((acc, cur) => {
                    acc += cur;
                    return acc;
                }, 0) / this.pressureList.length
                : undefined;
            let Spreading = {
                path: this.path,
                duration,
                concentration: concentration,
                irrigatedArea,
                points: irrigatedArea,
                output: amountSpread,
                startTime,
                endTime,
                name: 'SpreadingEvent:' + this.name + '_' + this.lastIrrigated,
                spreaderName: this.name,
                spreaderID: this.id,
                spreaderPK: this.pk, // THIS GOT INCORRECTELY OVEERWRITTEN
                distance,
                area,
                soilRisk: this.soilRisk,
                overlappedAreas,
                mappedRun: this.mappedRun,
                lastActive: endTime,
                runDifference: this.runDifference,
                pressure,
                applicationDepth,
                applicationDepthMM: applicationDepth * 10,
                pressureList: (_q = this.pressureList) !== null && _q !== void 0 ? _q : [],
                pumpState: (_r = this.pumpState) !== null && _r !== void 0 ? _r : [],
                moistureReadingsList: this.moistureReadingsList,
                isManuallyGenerated: manual,
                type: 'SpreadingEvent',
            };
            let Dead = {
                name: `Event Ended @${this.lastIrrigated}`, // `${effluentTracker.name} Ended @${updated_at}`,
                path: this.path,
                ttl: deleteTime,
                spreaderID: this.id,
                irrigatedArea,
                filteredPath: this.filteredPath,
                mappedPath: this.mappedPath,
                mappedRun: this.mappedRun,
            };
            return { Spreading, Dead };
        }
        else {
            throw new Error('No Path data was found, yet a new spreading event was attempted');
        }
    }
    isActive() {
        if (this.spreading && this.spreading === true && this.path && this.path.length > 0) {
            return true;
        }
        return false;
    }
    getUserAttributes() {
        return {
            ...super.getUserAttributes(),
            name: {
                type: 'String',
                editable: true,
                label: 'Name',
                name: 'name',
                description: 'Name of effluent tracker',
                required: true,
            },
            purpose: {
                type: 'String',
                editable: true,
                label: 'Purpose',
                name: 'purpose',
                description: 'Used for',
            },
            throwDistance: {
                type: 'Number',
                editable: true,
                label: 'Throw Radius Length (m)',
                name: 'throwDistance',
                description: 'Throw distance of effluent spreader',
                required: true,
            },
            flow: {
                type: 'Number',
                editable: true,
                label: 'Effluent Flow (L/min)',
                name: 'flow',
                description: 'The rate of liquid L/m',
                required: true,
            },
            location: {
                type: 'RelatedType',
                editable: false,
                label: 'Location',
                name: 'Location',
                description: 'Last position of the effluent spreader',
                objectTypeId: 'Location',
                direction: iotv_v3_types_1.PathDirection.child,
                edgeTypeId: 'EffluentTracker_has_Location',
            },
            comments: {
                type: 'String',
                editable: true,
                label: 'Comments',
                name: 'comments',
                description: 'Free text for info ',
            },
            location_lat: {
                type: 'Number',
                precision: 5,
                editable: false,
                limits: {
                    min: -90,
                    max: 90,
                },
            },
            location_lng: {
                type: 'Number',
                precision: 5,
                editable: false,
                limits: {
                    min: -180,
                    max: 180,
                },
            },
        };
    }
}
exports.EffluentTracker = EffluentTracker;
